import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import { Building2, Plane, ShieldCheck, MapPin, ArrowRight, Loader2, TrendingUp } from "lucide-react";

export default function Operators() {
  const [ops, setOps] = useState(null);

  useEffect(() => {
    api.get("/operators").then((r) => setOps(r.data.operators || [])).catch(() => setOps([]));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <SectionTitle
          overline="// CHARTER OPERATORS DIRECTORY"
          title="Fly with verified operators."
          subtitle="Part 135 charter companies with D085-verified ops-specs. Compare fleets, get direct quotes, or open a bid to the whole marketplace."
        />
        <div className="flex gap-2">
          <Link to="/quote" data-testid="cta-charter-request" className="btn btn-primary" style={{background:"#4B9CFF", borderColor:"#4B9CFF"}}>
            INSTANT PJ QUOTE <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link to="/operators/onboard" data-testid="cta-op-onboard" className="btn">
            <Building2 className="h-3.5 w-3.5" /> Add Your Company + Fleet
          </Link>
        </div>
      </div>

      {/* Empty Leg Program promo — pitched at both new + existing operators. */}
      <Link
        to="/empty-leg#operator"
        data-testid="operators-empty-leg-promo"
        className="block rounded-xl border border-amber-400/40 p-4 md:p-5 mb-6 relative overflow-hidden group hover:border-amber-400/70 transition-colors"
        style={{ background: "linear-gradient(155deg, rgba(251,191,36,0.10) 0%, rgba(5,8,16,0.85) 65%)" }}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md border border-amber-400/40 bg-amber-400/10">
              <TrendingUp className="h-5 w-5 text-amber-300" />
            </div>
            <div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-amber-300">// NEW · JUMPSEAT PROGRAM</div>
              <div className="font-display text-xl md:text-2xl font-bold text-white mt-0.5">Monetize your empty ferries — earn 15% of every rider fee.</div>
              <div className="text-[12px] text-white/60 mt-1 max-w-2xl">
                Riders pay <b className="text-white">$10,000/yr</b> for first-look access to your repositioning legs. You keep 100% of the seat fees you set, plus a quarterly cut of every rider's annual membership. Free to join · no exclusivity.
              </div>
            </div>
          </div>
          <div className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-amber-400/50 text-amber-300 group-hover:bg-amber-400/10 flex items-center gap-1.5 flex-shrink-0">
            ENROLL FLEET <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </div>
      </Link>

      {ops === null && (
        <div className="text-center py-16 text-white/50"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading operators…</div>
      )}
      {ops && ops.length === 0 && (
        <Card>
          <div className="text-center py-8">
            <Building2 className="h-10 w-10 text-white/30 mx-auto mb-3" />
            <div className="mono text-white/60 mb-4">No operators onboarded yet.</div>
            <Link to="/operators/onboard" className="btn btn-primary">Be the first — Onboard your fleet</Link>
          </div>
        </Card>
      )}
      {ops && ops.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {ops.map((op) => <OperatorCard key={op.id} op={op} />)}
        </div>
      )}
    </div>
  );
}

function OperatorCard({ op }) {
  const rp = op.role_profile || {};
  return (
    <Link to={`/operators/${op.id}`} data-testid={`op-card-${op.id}`} className="block">
      <Card className="hover:border-cyan/40 transition-colors group h-full">
        {rp.hero_photo_file_id ? (
          <div className="-mx-6 -mt-6 mb-4 aspect-[16/9] overflow-hidden rounded-t-2xl">
            <img src={`/api/files/${rp.hero_photo_file_id}`} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          </div>
        ) : (
          <div className="-mx-6 -mt-6 mb-4 aspect-[16/9] bg-gradient-to-br from-cyan/[0.10] to-transparent border-b border-cyan/10 flex items-center justify-center">
            <Plane className="h-8 w-8 text-cyan/40" />
          </div>
        )}
        <div className="flex items-start gap-3 mb-3">
          {rp.company_logo_file_id ? (
            <img src={`/api/files/${rp.company_logo_file_id}`} alt="" className="h-10 w-10 rounded-md bg-white/5 object-contain" />
          ) : (
            <div className="h-10 w-10 rounded-md bg-cyan/10 border border-cyan/30 flex items-center justify-center"><Building2 className="h-5 w-5 text-cyan" /></div>
          )}
          <div className="flex-1">
            <div className="font-display text-base font-semibold tracking-tight leading-tight">{rp.company_name || "Unnamed operator"}</div>
            <div className="mono text-[11px] text-white/50 mt-0.5">{rp.part_135_certificate || "—"}</div>
          </div>
          {rp.d085_verified && (
            <div className="mono text-[9px] px-1.5 py-0.5 rounded-full border border-avail/50 text-avail bg-avail/10 flex items-center gap-1"><ShieldCheck className="h-2.5 w-2.5" /> D085</div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/[0.06]">
          <MicroStat label="Fleet" v={op.fleet_count || 0} />
          <MicroStat label="Bases" v={(rp.primary_bases || []).length} />
          <MicroStat label="Safety" v={(rp.argus_wyvern_rating || "None").replace("ARGUS ", "").replace("WYVERN ", "W-")} small />
        </div>
      </Card>
    </Link>
  );
}

function MicroStat({ label, v, small }) {
  return (
    <div>
      <div className="eyebrow-muted mb-0.5">{label}</div>
      <div className={`mono text-cyan ${small ? "text-[11px]" : "text-base"}`}>{v}</div>
    </div>
  );
}
